import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './seasonal.css';
import { loadSettings } from '../components/cart/CartShippingTeaser';
import { getMode, isSeasonActive, INTENSITIES, MODE_KEYS } from './modes';
import { Garland } from './Garland';
import { Particles } from './Particles';
import { Accessory, Corners, BottomStrip, Sign } from './Pieces';

const HEADER_H = 64;

// Which mode is live: a `?season=<key>&intensity=<level>` URL preview wins, else the saved schedule.
export const useActiveSeason = () => {
  const { search } = useLocation();
  const [settings, setSettings] = useState(null);
  useEffect(() => { loadSettings().then(setSettings); }, []);
  const params = new URLSearchParams(search);
  const urlMode = params.get('season');
  if (urlMode && MODE_KEYS.includes(urlMode)) {
    const lvl = params.get('intensity');
    return { modeKey: urlMode, intensity: INTENSITIES.includes(lvl) ? lvl : 'normal' };
  }
  if (!settings || !isSeasonActive(settings)) return { modeKey: null, intensity: 'normal' };
  return { modeKey: settings.season_mode, intensity: INTENSITIES.includes(settings.season_intensity) ? settings.season_intensity : 'normal' };
};

const useWidth = (ref, fixed) => {
  const [width, setWidth] = useState(() => (fixed ? window.innerWidth : 0));
  useEffect(() => {
    if (fixed) {
      const onResize = () => setWidth(window.innerWidth);
      onResize();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, fixed]);
  return width;
};

// The decoration layer itself. `preview` renders it inside a relative box instead of fixed to the viewport.
// The header band (garland + sign) fades once the visitor scrolls so it never sits on top of content for long,
// and is skipped entirely on form-heavy pages.
const QUIET_PATHS = ['/checkout', '/login', '/register', '/account', '/forgot-password', '/reset-password'];

const useScrolled = (enabled) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 140);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled]);
  return enabled && scrolled;
};

export const SeasonalLayer = ({ modeKey, intensity = 'normal', preview = false, headerOffset = HEADER_H, quiet = false }) => {
  const ref = useRef(null);
  const width = useWidth(ref, !preview);
  const scrolled = useScrolled(!preview);
  const mode = getMode(modeKey);
  if (!mode) return null;
  const isMobile = width > 0 && width < 640;
  const seed = MODE_KEYS.indexOf(modeKey) + 1;
  return (
    <div ref={ref} className={`szn-layer ${preview ? 'szn-preview' : ''}`} aria-hidden="true" data-testid={`seasonal-overlay-${modeKey}`} data-intensity={intensity} data-scrolled={scrolled ? 'true' : 'false'}>
      {width > 0 && (
        <>
          <Particles particles={mode.particles} intensity={intensity} isMobile={isMobile} preview={preview} seed={seed} />
          {!quiet && (
            <div className="szn-band" style={{ top: headerOffset, opacity: scrolled ? 0.3 : 1 }} data-testid="szn-header-band">
              <Garland garland={mode.garland} width={width} isMobile={isMobile} intensity={intensity} />
              <Sign sign={mode.sign} isMobile={isMobile} headerOffset={0} />
            </div>
          )}
          <Corners corners={mode.corners} intensity={intensity} isMobile={isMobile} headerOffset={headerOffset} />
          <BottomStrip kind={mode.bottom} isMobile={isMobile} />
        </>
      )}
    </div>
  );
};

// Mounted once in the public Layout: fixed, click-through, follows the admin schedule.
export const SeasonalOverlay = () => {
  const { modeKey, intensity } = useActiveSeason();
  const { pathname } = useLocation();
  if (!modeKey) return null;
  const quiet = QUIET_PATHS.some((p) => pathname.startsWith(p));
  return <SeasonalLayer modeKey={modeKey} intensity={quiet ? 'subtle' : intensity} quiet={quiet} />;
};

// Hat / emoji worn by the header logo (header is sticky, so it stays put while scrolling).
export const SeasonalLogoAccessory = ({ size = 48 }) => {
  const { modeKey } = useActiveSeason();
  const mode = getMode(modeKey);
  if (!mode?.accessory) return null;
  return <Accessory accessory={mode.accessory} size={size} />;
};

export default SeasonalOverlay;
